import { useMutation, useQuery } from "@apollo/client";
import { FilmIcon } from "@heroicons/react/24/outline";
import {
  HandThumbDownIcon,
  HandThumbUpIcon,
  PlayIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionButton from "../../components/ActionButton/ActionButton";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  MediaActionRow,
  MediaActions,
  MediaCard,
  MediaCornerActions,
  MediaFrame,
  MediaGrid,
  MediaIconChip,
  MediaImage,
  MediaTop,
} from "../../components/Styled/Media";
import { Video } from "../../graphql/graphql";
import { APPROVE_VIDEO } from "../../graphql/mutations-gql";
import { UNVERIFIED_VIDEOS } from "../../graphql/queries-gql";

const PAGE_SIZE = 20;

const UnverifiedVideos = () => {
  const { loading, error } = useQuery(UNVERIFIED_VIDEOS, {
    fetchPolicy: "network-only",
    variables: {
      paging: {
        type: "page",
        pageArgs: {
          page: 1,
          limit: PAGE_SIZE,
        },
      },
    },
    onCompleted: (data) => {
      console.log("fetchVideos onCompleted", data);
      setVideos(data.unverifiedVideos.nodes);
    },
    onError: (error) => {
      console.log("fetchVideos onError", error);
    },
  });
  const [videos, setVideos] = useState<Video[]>([]);
  const [approveVideo] = useMutation(APPROVE_VIDEO);
  const navigate = useNavigate();

  const showImage = (src: string) => {
    //window.location.href = src;
    window.open(src, "_blank");
  };

  const handleApprove = (value: boolean, uploadId: string) => {
    approveVideo({
      variables: {
        uploadId,
        value,
      },
      onCompleted: (data) => {
        // Completion means we should remove the photo.
        const newVideos = videos.filter((video) => video.uploadId !== uploadId);
        console.log("approveVideo onCompleted", data);
        setVideos(newVideos);
      },
      onError: (error) => {
        console.log("approveVideo onError", error);
      },
    });
  };

  return (
    <Page
      title="Public videos"
      description="Videos waiting for review. Hover a video to approve or reject it, click to play."
      badge={
        videos.length > 0 && (
          <Badge $tone="primary">{videos.length} waiting</Badge>
        )
      }
    >
      {error && <ErrorAlert error={error} />}

      {!loading && !error && videos.length === 0 && (
        <EmptyState
          Icon={FilmIcon}
          title="No videos to review"
          description="New public videos show up here."
        />
      )}

      {videos && videos.length > 0 && (
        <MediaGrid $min={200} $gap={12}>
          {videos.map((video) => {
            return (
              <MediaCard key={video.uploadId}>
                <MediaFrame $ratio="9 / 16" onClick={() => showImage(video.url)}>
                  <MediaImage
                    src={video.thumbnailUrl ?? ""}
                    alt=""
                    loading="lazy"
                  />
                  <MediaTop>
                    <span />
                    <MediaIconChip title="Video">
                      <PlayIcon width={13} />
                    </MediaIconChip>
                  </MediaTop>
                  <MediaCornerActions>
                    <ActionButton
                      label="Open user"
                      size={30}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/user/${video.uploadById}`);
                      }}
                    >
                      <UserIcon />
                    </ActionButton>
                  </MediaCornerActions>
                  <MediaActions>
                    <MediaActionRow>
                      <ActionButton
                        label="Approve"
                        tone="approve"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(true, video.uploadId);
                        }}
                      >
                        <HandThumbUpIcon />
                      </ActionButton>
                      <ActionButton
                        label="Reject"
                        tone="reject"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(false, video.uploadId);
                        }}
                      >
                        <HandThumbDownIcon />
                      </ActionButton>
                    </MediaActionRow>
                  </MediaActions>
                </MediaFrame>
              </MediaCard>
            );
          })}
        </MediaGrid>
      )}

      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}
    </Page>
  );
};
export default UnverifiedVideos;
